import React, { useState, useRef } from 'react';
import { Building, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import FileUploadSection from './FileUploadSection';
import { inputClass, selectClass, labelClass } from './vaultUtils';

const PropertyEntryModal = ({ mode, entry, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    save_as: entry?.save_as || '',
    property_type: entry?.data?.property_type || '',
    ownership_type: entry?.data?.ownership_type || '',
    property_address: entry?.data?.property_address || ''
  });

  const [files, setFiles] = useState(entry?.files || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.save_as.trim()) {
      toast.error('Please enter a name to save as');
      return;
    }
    onSave(formData, pendingFiles);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="property-entry-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-cyan-500/10">
              <Building className="w-5 h-5 text-cyan-400" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {mode === 'add' ? 'Add Property' : 'Edit Property'}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-2 hover:bg-slate-800 rounded-lg">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] space-y-6">
          <FileUploadSection 
            mode={mode} 
            entry={entry} 
            files={files} 
            setFiles={setFiles} 
            pendingFiles={pendingFiles} 
            setPendingFiles={setPendingFiles} 
            uploading={uploading} 
            setUploading={setUploading} 
            fileInputRef={fileInputRef} 
          />

          <div className="space-y-2">
            <Label className={labelClass}>Save As *</Label>
            <Input name="save_as" value={formData.save_as} onChange={handleChange} placeholder="e.g., Mumbai Flat" className={inputClass} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className={labelClass}>Property Type</Label>
              <select name="property_type" value={formData.property_type} onChange={handleChange} className={selectClass}>
                <option value="">Select</option>
                <option value="Residential Flat">Residential Flat</option>
                <option value="House">House</option>
                <option value="Villa">Villa</option>
                <option value="Land/Plot">Land/Plot</option>
                <option value="Commercial Property">Commercial Property</option>
                <option value="Shop">Shop</option>
                <option value="Office Space">Office Space</option>
                <option value="Agricultural Land">Agricultural Land</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className={labelClass}>Ownership Type</Label>
              <select name="ownership_type" value={formData.ownership_type} onChange={handleChange} className={selectClass}>
                <option value="">Select</option>
                <option value="Single">Single Owner</option>
                <option value="Joint">Joint Ownership</option>
                <option value="Family">Family Owned</option>
                <option value="Inherited">Inherited</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Property Address</Label>
            <textarea
              name="property_address"
              value={formData.property_address}
              onChange={handleChange}
              rows="3"
              placeholder="Complete address with survey/plot number"
              className={`${inputClass} w-full rounded-md p-2`}
            ></textarea>
          </div>
        </form>

        <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
          <Button type="submit" onClick={handleSubmit} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold">
            {saving ? <><Loader2 size={16} className="mr-2 animate-spin" /> Saving...</> : mode === 'add' ? 'Add Entry' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PropertyEntryModal;
